import { utils } from '@digital-commerce/shared-utils'
import {
  pipe,
  path,
  isEmpty,
  propOr,
  filter,
  prop,
  allPass,
  includes,
  complement,
  intersperse,
  update,
  join,
  find,
  defaultTo,
  propEq,
  anyPass,
  sortBy,
  isNil,
  memoizeWith,
  evolve,
  map
} from 'ramda'
import {
  getCacheKey,
  getHSCredits,
  getRecurringPromotionsWhereDurationIsLessThanItemRecurringDuration,
  groupMonthlyItemsByOfferId,
  isMonthlyRecurringAmountNotZero
} from '../../../utils/cart'
import { formatCurrency } from '../../../utils/currency'
import {
  buildHSGroupedRecurringDiscountList,
  buildIRPCMonthlyDiscountsList,
  buildIRPCOneTimeDiscountsList,
  getDiscountDisplayName
} from '../../../utils/discount'
import {
  CONSTANTS,
  getHSEppDiscount,
  formatHSEppDiscount,
  getInstallType,
  getServiceWithoutSpecifiedDiscounts,
  getSubItemSortingWeight,
  getTVExtras
} from '../../../utils/homeSolutions'

const { hasContractTerm, getDisplayName, getMonthlyItemPrice, getHSService } = utils.cart

const CHANGE_URLS = {
  AUTHENTICATED_USER_CHANGE_URL: '/my-account/manage-services/home/optik/packages',
  AUTHENTICATED_USER_MANAGE_CHANNEL_CHANGE_URL: '/my-telus/tv/optik/package-selection',
  UNAUTHENTICATED_USER_CHANGE_URL: '/shop/home-services/bundle/build-your-own'
}

export const getOptikTvService = memoizeWith(getCacheKey('HS-OptikTV'), (cart) => {
  if (!cart?.root?.items) return undefined

  return pipe(
    getHSService(['optik', 'tv']),
    evolve({
      items: map((id) => path(['items', id], cart))
    })
  )(cart)
})

export const getLegalFootnoteKeys = (service) => {
  if (hasContractTerm(service)) {
    return ['cancellationFeeLegalSlug', 'yearlyPVRAndDigitalBoxLegalSlug', 'rentalEquipmentSlug']
  }
  return ['noTermcancellationFeeLegalSlug', 'monthlyPVRAndDigitalBoxLegalSlug']
}

export const getChangeUrl = (isAuthenticated, isIrpcFlow, isManageChannelFlow) => {
  // IRPC flows
  if (isIrpcFlow) return CONSTANTS.FIFA_IRPC.FIFA_IRPC_CHANGE_URL
  // Authenticated manage channels
  if (isAuthenticated && isManageChannelFlow)
    return CHANGE_URLS.AUTHENTICATED_USER_MANAGE_CHANNEL_CHANGE_URL
  // Authenticated
  if (isAuthenticated) return CHANGE_URLS.AUTHENTICATED_USER_CHANGE_URL

  // Unauthenticated
  return CHANGE_URLS.UNAUTHENTICATED_USER_CHANGE_URL
}

const serviceItemHasTag = (service, tag) => {
  return pipe(propOr([], 'items'), filter(pipe(prop('tags'), allPass([includes(tag)]))))(service)
}

const getServiceText = (service, translate, tag) => {
  return !isEmpty(serviceItemHasTag(service, tag)) ? translate(`${tag}`) : ''
}

const getAllServiceTexts = (service, translate) => {
  const themePack = getServiceText(service, translate, 'themepack')
  const premium = getServiceText(service, translate, 'premium')
  const additionalChannels = getServiceText(service, translate, 'channel')

  return filter(complement(isEmpty), [themePack, premium, additionalChannels])
}

const getDescriptionAttribute = (service, translate) => {
  const servicesText = getAllServiceTexts(service, translate)
  const essentialsText = translate('essentials')

  const attribute = [essentialsText, ...servicesText]

  // Joins text together with the "and" conjunction if there are multiple
  const serviceAttribute = pipe(
    intersperse(', '),
    update(-2, translate('andConjunction')),
    join('')
  )(attribute)

  return serviceAttribute
}

const getTvCountTranslationProps = (topLevelItem) => {
  const optikTVCount = pipe(
    path(['productOffering', 'characteristics']),
    find(propEq('name', 'optikTVCount')),
    prop('value')
  )(topLevelItem)

  const tvCount = parseInt(
    defaultTo(
      optikTVCount,
      pipe(
        path(['productOffering', 'characteristics']),
        find(propEq('name', '9148465520113089778')),
        prop('displayValue')
      )(topLevelItem)
    ),
    10
  )

  return tvCount
    ? {
        path: tvCount === 1 ? 'tvCountSingular' : 'tvCount',
        tvCount
      }
    : null
}

export const getAttributes = (service, translate, cart) => {
  const attributes = []

  // Description attribute
  const descriptionAttribute = getDescriptionAttribute(service, translate)
  attributes.push(descriptionAttribute)

  // Connected TVs attribute
  const tvConnectedProps = getTvCountTranslationProps(service)
  if (tvConnectedProps) {
    const connectedText = translate(tvConnectedProps.path, {
      replacements: { tvCount: tvConnectedProps.tvCount }
    })
    attributes.push(connectedText)
  }

  // Credits attribute
  const credits = getHSCredits('optik')(cart)
  credits.forEach((credit) => attributes.push(getDisplayName(credit)))

  // Installation type attribute for OPUS
  const isOpus = pipe(
    prop('tags'),
    allPass([includes('optik4'), includes('tv')])
  )({ tags: service.tags })
  if (isOpus) {
    const installType =
      getInstallType(service) === 'SELF_INSTALL'
        ? translate('item.selfInstall')
        : translate('item.proInstall')
    attributes.push(installType)
  }

  return attributes
}

const buildSubItemsList = (subItems, lang, showQuantity = false) => {
  const mapper = (featureItem) => {
    const quantity = showQuantity ? `${featureItem.quantity} ` : ''
    const { id } = featureItem
    const name = `${quantity}${getDisplayName(featureItem)}`
    const price = `${formatCurrency(getMonthlyItemPrice(featureItem).subTotal, lang)}/mo.`

    return {
      id,
      name,
      price
    }
  }

  return subItems?.map(mapper)
}

const getOptikTVSubItems = pipe(
  prop('items'),
  filter(pipe(prop('tags'), complement(includes('hardware')))),
  filter(
    pipe(
      prop('tags'),
      anyPass([
        allPass([includes('tv'), includes('optik'), includes('premium')]),
        allPass([includes('tv'), includes('optik'), includes('themepack')]),
        allPass([includes('tv'), includes('optik'), includes('channel')])
      ])
    )
  ),
  filter(complement(isMonthlyRecurringAmountNotZero)),
  sortBy(getSubItemSortingWeight)
)

export const buildServiceSubItemsList = (service, lang) => {
  const subItems = getOptikTVSubItems(service)
  return buildSubItemsList(subItems, lang)
}

export const buildRecurringDiscountsList = (service, isIrpcFlow, lang, translate, isAffiliates) => {
  if (isIrpcFlow) return buildIRPCMonthlyDiscountsList(service, translate, lang, isAffiliates)

  // Business rule: EPP discount must be displayed as a separate line item, and is displayed last
  const eppDiscount = getHSEppDiscount(service)
  if (eppDiscount) {
    const serviceWithoutEppDiscount = getServiceWithoutSpecifiedDiscounts(service, [eppDiscount])
    return [
      ...buildHSGroupedRecurringDiscountList(serviceWithoutEppDiscount, lang, translate, false),
      formatHSEppDiscount(eppDiscount, translate, lang, isAffiliates)
    ]
  }

  return buildHSGroupedRecurringDiscountList(service, lang, translate, false)
}

export const buildAddonsList = (service, lang) => {
  const addons = pipe(
    prop('items'),
    filter(pipe(prop('tags'), allPass([includes('addon'), includes('optik')])))
  )(service)

  return buildSubItemsList(addons, lang)
}

export const buildExtrasList = (service, lang) => {
  const extras = getTVExtras(service)
  return buildSubItemsList(extras, lang)
}

const getOptikTVHardware = pipe(prop('items'), filter(pipe(prop('tags'), includes('hardware'))))

export const buildIncludedHardwareList = (service, lang) => {
  const includedHardware = pipe(
    getOptikTVHardware,
    filter((item) => getMonthlyItemPrice(item).amount === 0)
  )(service)

  return buildSubItemsList(includedHardware, lang, true)
}

export const buildAdditionalHardwareList = (service, lang) => {
  const additionalHardware = pipe(
    getOptikTVHardware,
    filter((item) => getMonthlyItemPrice(item).amount > 0),
    groupMonthlyItemsByOfferId
  )(service)

  return buildSubItemsList(additionalHardware, lang, true)
}

export const buildOneTimeDiscountsList = (service, lang, isIrpcFlow) => {
  if (isIrpcFlow) return buildIRPCOneTimeDiscountsList(service, lang)

  const topLevelDiscounts = getRecurringPromotionsWhereDurationIsLessThanItemRecurringDuration([
    service
  ])
  const subLevelDiscounts = getRecurringPromotionsWhereDurationIsLessThanItemRecurringDuration(
    service.items
  )

  const oneTimeDiscounts = [...topLevelDiscounts, ...subLevelDiscounts]

  return oneTimeDiscounts?.map((discount, idx) => {
    const id = discount.id || discount.promotionId || `${discount[idx].id}-${idx}`

    return {
      id,
      name: getDiscountDisplayName(discount),
      price: `-${formatCurrency(discount.amount, lang)}`,
      prefixIcon: discount.isCouponCode ? 'PIGGYBANK' : 'DEALS'
    }
  })
}

export const shouldRender = (cart) => {
  const service = getOptikTvService(cart)
  return !isNil(service)
}
